"use client";

import { useTranslations } from "next-intl";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import CheckBox from "@/components/ui/CheckBox";

// Persian/Arabic letters that users type interchangeably, plus the
// zero-width non-joiner, are folded away before matching.
const normalize_query = (value) =>
	String(value ?? "")
		.replace(/[\u064A\u0649]/g, "\u06CC")
		.replace(/\u0643/g, "\u06A9")
		.replace(/[\u200C\u200F\u200E]/g, "")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const CHIP_CLASS = "rounded-sm bg-jmt-neutral-100 px-1.5 py-0.5 text-xs leading-4 text-jmt-main";
const CHIP_GAP = 4;

const Index = ({
	options,
	disabled = false,
	label,
	placeholder,
	id,
	name,
	input_style,
	label_style,
	label_toolbar,
	required,
	className,
	value,
	onChange,
	errors,
	searchable = false,
	search_placeholder = "جستجو کنید",
	empty_text = "موردی یافت نشد",
	// انتخاب چندتایی: value آرایه‌ای از value گزینه‌هاست، منو با هر انتخاب بسته
	// نمی‌شود و گزینه‌های انتخاب‌شده به شکل چیپ داخل فیلد نشان داده می‌شوند.
	multiple = false,
	// حداکثر چیپی که در یک خط نشان داده می‌شود؛ بقیه به شکل «+n» می‌آیند
	max_visible_chips = 3,
	// اگر زیر فیلد (با احتساب نوار ثابت پایین صفحه) جای منو نباشد، منو به سمت بالا باز می‌شود
	auto_flip = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selected_option, set_selected_option] = useState(value ?? "");
	const [search_query, set_search_query] = useState("");
	const [open_up, set_open_up] = useState(false);
	const containerRef = useRef(null);
	const searchRef = useRef(null);
	const t = useTranslations();

	useEffect(() => {
		set_selected_option(value ?? "");
	}, [value]);

	const selected_values = multiple
		? (Array.isArray(selected_option) ? selected_option : []).map(String)
		: [];

	// چیپ‌ها همیشه در یک خط می‌مانند: تعداد چیپی که در عرض فیلد جا می‌شود (حداکثر
	// max_visible_chips) از روی یک ردیف نامرئی اندازه‌گیری می‌شود و بقیه در «+n» جمع می‌شوند.
	const chips_ref = useRef(null);
	const meter_ref = useRef(null);
	const [visible_chips, set_visible_chips] = useState(max_visible_chips);
	const chips_key = selected_values.join("|");

	useIsoLayoutEffect(() => {
		if (!multiple) return;
		const row = chips_ref.current;
		const meter = meter_ref.current;
		if (!row || !meter) return;

		const measure = () => {
			const chips = [...meter.querySelectorAll("[data-chip]")];
			const more = meter.querySelector("[data-more]");
			const total = chips.length;
			const limit = Math.min(total, max_visible_chips);
			let used = 0;
			let fit = 0;
			for (let i = 0; i < limit; i += 1) {
				used += chips[i].offsetWidth + (i > 0 ? CHIP_GAP : 0);
				const needed = used + (i + 1 < total ? CHIP_GAP + (more?.offsetWidth || 0) : 0);
				if (needed > row.clientWidth) break;
				fit = i + 1;
			}
			// دست‌کم یک چیپ نشان داده می‌شود (در صورت نیاز با متن کوتاه‌شده)
			set_visible_chips(Math.max(1, fit));
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(row);
		return () => observer.disconnect();
	}, [multiple, chips_key, max_visible_chips, options]);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target))
				setIsOpen(false);
		};
		document.addEventListener("pointerdown", handleClickOutside, true);
		return () => document.removeEventListener("pointerdown", handleClickOutside, true);
	}, []);

	useIsoLayoutEffect(() => {
		if (!isOpen || !auto_flip) {
			set_open_up(false);
			return;
		}
		const container = containerRef.current;
		if (!container) return;
		const footer = parseInt(getComputedStyle(container).getPropertyValue("--page-fixed-footer-height"), 10) || 0;
		const rect = container.getBoundingClientRect();
		const menu_height = 45 * 4 + 16 + (searchable ? 40 : 0) + 8;
		const space_below = window.innerHeight - footer - rect.bottom;
		set_open_up(space_below < menu_height && rect.top > space_below);
	}, [isOpen, auto_flip, searchable]);

	// the search box only lives while the menu is open, so reset it on close
	useEffect(() => {
		if (!isOpen) {
			set_search_query("");
			return;
		}
		if (searchable) {
			const timer = setTimeout(() => searchRef.current?.focus(), 0);
			return () => clearTimeout(timer);
		}
	}, [isOpen, searchable]);

	const handle_select = (val) => {
		if (multiple) {
			const key = String(val);
			const next = selected_values.includes(key)
				? selected_values.filter((item) => item !== key)
				: [...selected_values, key];
			set_selected_option(next);
			onChange?.({ target: { name, id, value: next } });
			return;
		}
		set_selected_option(val);
		setIsOpen(false);
		onChange?.({ target: { name, id, value: val } });
	};

	const currentSelectedOption = options?.find(
		(item) => String(item?.value ?? item) === String(selected_option)
	);

	const getOptionLabel = (item) => {
		if (!item) return "";
		const labelText = item?.label ?? item?.name ?? item;
		return labelText?.includes?.(".") ? (t(labelText) || t(item)) : (labelText || item);
	};

	const get_chip_label = (key) =>
		getOptionLabel(options?.find((item) => String(item?.value ?? item) === key)) || key;

	const getRawLabel = (item) => String(item?.label ?? item?.name ?? item ?? "");

	const filtered_options = useMemo(() => {
		if (!searchable) return options || [];
		const query = normalize_query(search_query);
		if (!query) return options || [];
		return (options || []).filter((item) =>
			normalize_query(getRawLabel(item)).includes(query)
		);
	}, [options, search_query, searchable]);

	const handle_search_key_down = (e) => {
		if (e.key === "Escape") {
			e.preventDefault();
			setIsOpen(false);
			return;
		}
		if (e.key === "Enter") {
			e.preventDefault();
			const first = filtered_options?.[0];
			if (first) handle_select(first?.value ?? first);
		}
	};

	const hasError = errors?.length > 0;

	// ارور یا کلید ترجمه است یا متن آماده‌ی فارسی که ولیدیتورها برمی‌گردانند
	const error_message = (error) => {
		if (!error) return "";
		return typeof error === "string" && t.has(error) ? t(error) : error;
	};

	return (
		<div
			ref={containerRef}
			className={`relative w-full mx-auto ${className || ""}`}
			dir="rtl"
		>
			{label && (
				<div className="w-full flex items-center justify-between gap-1 min-h-5 mb-1.5">
					<label
						htmlFor={id}
						className={`relative text-sm font-medium ${disabled ? "text-jmt-neutral-500" : "text-jmt-main"} ${label_style || ""}`}
					>
						{label}
						{required && (
							<span className="absolute top-0 -left-2 text-jmt-red-500">*</span>
						)}
					</label>
					{label_toolbar}
				</div>
			)}

			<div
				className={`flex items-center w-full border rounded-md overflow-hidden transition-all duration-150
		${hasError
						? "border-red-500"
						: disabled
							? "bg-jmt-neutral-50 border-jmt-neutral-200 cursor-not-allowed"
							: isOpen
								? "border-jmt-neutral-600"
								: "border-jmt-neutral-300 hover:border-jmt-neutral-500"
					}
		${input_style || ""}`}
			>

				{multiple ? (
					// دکمه‌ی حذف چیپ داخل تریگر است و دکمه داخل دکمه مجاز نیست، پس
					// تریگر در این حالت یک div قابل فوکوس است.
					<div
						id={id}
						role="combobox"
						aria-expanded={isOpen}
						aria-controls={`${id || name}-listbox`}
						aria-disabled={disabled}
						tabIndex={disabled ? -1 : 0}
						onClick={() => !disabled && setIsOpen(!isOpen)}
						onKeyDown={(e) => {
							if (disabled || (e.key !== "Enter" && e.key !== " ")) return;
							e.preventDefault();
							setIsOpen(!isOpen);
						}}
						className={`w-full flex items-center justify-between gap-2 bg-transparent outline-0 text-sm min-h-9 text-right
							${selected_values.length > 0 ? "py-1.5 ps-1.5 pe-3" : "py-2 px-3"}
							${disabled ? "text-jmt-neutral-500 cursor-not-allowed" : "cursor-pointer"}`}
					>
						{selected_values.length > 0 ? (
							<>
								<span ref={chips_ref} className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
									{selected_values.slice(0, visible_chips).map((key) => {
										const option_label = get_chip_label(key);
										return (
											<span key={key} className={`flex min-w-0 items-center gap-1 ${CHIP_CLASS}`}>
												<span className="truncate">{option_label}</span>
												<button
													type="button"
													disabled={disabled}
													aria-label={`حذف ${option_label}`}
													onClick={(e) => {
														e.stopPropagation();
														handle_select(key);
													}}
													className="flex shrink-0 items-center justify-center cursor-pointer"
												>
													<X size={12} className="text-jmt-neutral-600" />
												</button>
											</span>
										);
									})}
									{selected_values.length > visible_chips && (
										<span
											dir="ltr"
											title={selected_values.slice(visible_chips).map(get_chip_label).join("، ")}
											className={`shrink-0 ${CHIP_CLASS}`}
										>
											+{selected_values.length - visible_chips}
										</span>
									)}
								</span>

								{/* ردیف اندازه‌گیری: نامرئی و بریده‌شده تا روی چیدمان و اسکرول صفحه اثر نگذارد */}
								<span aria-hidden="true" className="pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-hidden">
									<span ref={meter_ref} className="flex w-max items-center gap-1 whitespace-nowrap">
										{selected_values.map((key) => (
											<span key={key} data-chip className={`flex items-center gap-1 ${CHIP_CLASS}`}>
												{get_chip_label(key)}
												<span className="size-3" />
											</span>
										))}
										<span data-more dir="ltr" className={CHIP_CLASS}>
											+{selected_values.length}
										</span>
									</span>
								</span>
							</>
						) : (
							<span className="truncate text-jmt-main text-sm">
								{placeholder || t("choose") || "انتخاب کنید"}
							</span>
						)}
						<svg
							className={`w-4 h-4 text-jmt-neutral-600 transition-transform duration-200 mr-2 shrink-0 ${isOpen ? "rotate-180" : ""}`}
							fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
						</svg>
					</div>
				) : (
				<button
					id={id}
					type="button"
					disabled={disabled}
					onClick={() => setIsOpen(!isOpen)}
					className={`w-full flex items-center justify-between bg-transparent outline-0 text-sm py-2 px-3 min-h-9 text-right
						${disabled ? "text-jmt-neutral-500 cursor-not-allowed" : "cursor-pointer"}`}
				>
					<span className="truncate text-jmt-main text-sm">
						{selected_option
							? getOptionLabel(currentSelectedOption)
							: (placeholder || t("choose") || "انتخاب کنید")}
					</span>
					<svg
						className={`w-4 h-4 text-jmt-neutral-600 transition-transform duration-200 mr-2 shrink-0 ${isOpen ? "rotate-180" : ""}`}
						fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
					</svg>
				</button>
				)}
			</div>

			{isOpen && !disabled && (
				<div
					className={`absolute z-30 w-full ${open_up ? "bottom-full mb-1" : "mt-1"} bg-jmt-white border border-jmt-neutral-200 rounded-md shadow-sm overflow-hidden text-right`}
					style={{ direction: "rtl" }}
				>
					{searchable && (
						<div className="flex items-center gap-2 px-2 py-1.5 border-b border-jmt-neutral-100">
							<Search className="w-4 h-4 shrink-0 text-jmt-neutral-400" />
							<input
								ref={searchRef}
								type="text"
								value={search_query}
								onChange={(e) => set_search_query(e.target.value)}
								onKeyDown={handle_search_key_down}
								placeholder={search_placeholder}
								className="w-full bg-transparent outline-0 text-sm text-jmt-main placeholder:text-jmt-neutral-400 placeholder:text-[13px]"
							/>
							{search_query && (
								<button
									type="button"
									onClick={() => {
										set_search_query("");
										searchRef.current?.focus();
									}}
									className="flex items-center justify-center cursor-pointer"
									aria-label="پاک کردن جستجو"
								>
									<X size={16} className="text-jmt-neutral-400 hover:text-jmt-neutral-600" />
								</button>
							)}
						</div>
					)}

					<ul
						id={multiple ? `${id || name}-listbox` : undefined}
						role={multiple ? "listbox" : undefined}
						aria-multiselectable={multiple || undefined}
						className="p-1 max-h-45 overflow-y-auto custom-scrollbar"
					>
						{placeholder && !search_query && !multiple && (
							<li
								onClick={() => handle_select("")}
								className="p-2 text-sm text-jmt-main rounded-md cursor-pointer hover:bg-jmt-neutral-100 transition-colors"
							>
								{placeholder}
							</li>
						)}
						{filtered_options?.map((item, index) => {
							const itemValue = item?.value ?? item;
							const isSelected = multiple
								? selected_values.includes(String(itemValue))
								: String(itemValue) === String(selected_option);
							if (multiple) {
								// تیک فقط نمایشی است؛ کلیک روی کل ردیف انتخاب را عوض می‌کند.
								return (
									<li
										key={index}
										role="option"
										aria-selected={isSelected}
										onClick={() => handle_select(itemValue)}
										className="p-2 rounded-md cursor-pointer hover:bg-jmt-neutral-100 transition-colors"
									>
										<CheckBox
											checked={isSelected}
											label={getOptionLabel(item)}
											tabIndex={-1}
											className="pointer-events-none"
										/>
									</li>
								);
							}
							return (
								<li
									key={index}
									onClick={() => handle_select(itemValue)}
									className={`p-2 text-sm text-jmt-main rounded-md cursor-pointer hover:bg-jmt-neutral-100 transition-colors ${isSelected ? "font-bold" : ""}`}
								>
									{getOptionLabel(item)}
								</li>
							);
						})}
						{searchable && search_query && filtered_options?.length === 0 && (
							<li className="p-2 text-sm text-jmt-neutral-400 text-center">
								{empty_text}
							</li>
						)}
					</ul>
				</div>
			)}

			{hasError && (
				<div className="absolute text-[10px] flex ms-2 text-jmt-red-500 animate-fade mt-1">
					{error_message(typeof errors === "string" ? errors : errors?.[0])}
				</div>
			)}
		</div>
	);
};

export default Index;
